import { CommonModule, DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import {
  PropertyDto,
  PropertyMemberDto,
  PropertyMemberRole,
  PropertyMemberStatus,
  UserDto,
} from '@workspace/data-types';
import { AvatarAuraComponent, DialogLayoutAuraComponent } from '@workspace/ng-aura';
import { UserStore } from '@workspace/ng-data-access';

export type PropertyInvitationDialogResult = 'accept' | 'decline';

@Component({
  selector: 'etxe-property-invitation-details-dialog',
  standalone: true,
  imports: [
    CommonModule,
    DecimalPipe,
    MatButtonModule,
    MatIconModule,
    MatDialogModule,
    DialogLayoutAuraComponent,
    AvatarAuraComponent,
  ],
  templateUrl: './property-invitation-details-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertyInvitationDetailsDialogComponent {
  readonly property: PropertyDto = inject(MAT_DIALOG_DATA);
  readonly dialogRef = inject(
    MatDialogRef<PropertyInvitationDetailsDialogComponent, PropertyInvitationDialogResult>,
  );
  private readonly userStore = inject(UserStore);

  readonly PropertyMemberRole = PropertyMemberRole;

  readonly currentMember = computed<PropertyMemberDto | undefined>(() => {
    const user = this.userStore.current();
    const currentUserId = user?._id?.toString();
    const currentUserEmail = user?.contactInfos?.email?.toLowerCase();
    const members = this.property?.members || [];

    const pending = members.find((m) => {
      if (m.status !== PropertyMemberStatus.PENDING) return false;
      const mUserId =
        typeof m.user === 'object' && m.user && '_id' in m.user
          ? (m.user as UserDto)._id?.toString()
          : m.user?.toString();
      const mEmail = m.email?.toLowerCase();
      return (
        (currentUserId && mUserId === currentUserId) || (currentUserEmail && mEmail === currentUserEmail)
      );
    });

    if (pending) return pending;

    return members.find((m) => {
      const mUserId =
        typeof m.user === 'object' && m.user && '_id' in m.user
          ? (m.user as UserDto)._id?.toString()
          : m.user?.toString();
      const mEmail = m.email?.toLowerCase();
      return (
        (currentUserId && mUserId === currentUserId) || (currentUserEmail && mEmail === currentUserEmail)
      );
    });
  });

  readonly role = computed<PropertyMemberRole>(() => {
    return this.currentMember()?.role || PropertyMemberRole.CO_OWNER;
  });

  readonly isViewer = computed<boolean>(() => {
    return this.role() === PropertyMemberRole.VIEWER;
  });

  readonly roleLabel = computed<string>(() => {
    return this.isViewer() ? 'Spectateur' : 'Co-vendeur';
  });

  readonly dialogTitle = computed<string>(() => {
    return this.isViewer() ? "Invitation d'accès spectateur" : 'Invitation à la co-gestion';
  });

  readonly coverPhotoUrl = computed<string | null>(() => {
    const cp = this.property?.media?.coverPhoto;
    if (cp && typeof cp === 'object' && 'url' in cp && typeof cp.url === 'string') {
      return cp.url;
    }
    return null;
  });

  readonly price = computed<number | null>(() => {
    const cents = this.property?.financials?.sellingPriceCents;
    if (typeof cents === 'number' && cents > 0) {
      return cents / 100;
    }
    return null;
  });

  readonly surface = computed<number | null>(() => {
    return this.property?.keyInfo?.surface?.habitable || null;
  });

  readonly roomCount = computed<number | null>(() => {
    const interior = this.property?.characteristics?.interior;
    if (!interior) return null;
    let count = 0;
    if (Array.isArray(interior.bedroom)) {
      count += interior.bedroom.length;
    }
    if (Array.isArray(interior.rooms)) {
      count += interior.rooms.length;
    }
    return count > 0 ? count : null;
  });

  readonly inviterName = computed<string>(() => {
    const owner = this.property?.owner;
    if (owner?.identity?.firstName) {
      return `${owner.identity.firstName} ${owner.identity.lastName || ''}`.trim();
    }
    return 'Le propriétaire';
  });

  readonly inviterAvatar = computed<string | null>(() => {
    const avatar = this.property?.owner?.identity?.avatar;
    if (avatar && typeof avatar === 'object' && 'url' in avatar && typeof avatar.url === 'string') {
      return avatar.url;
    }
    return null;
  });

  readonly propertyTypeLabel = computed<string>(() => {
    const type = this.property?.definition?.propertyType;
    const labels: Record<string, string> = {
      apartment: 'Appartement',
      house: 'Maison',
      land: 'Terrain',
      parking_box: 'Parking / Box',
      business: 'Fonds de commerce',
      professional: 'Immobilier professionnel',
    };
    return (type && labels[type]) || 'Bien immobilier';
  });

  readonly dpeColor = computed<string>(() => {
    const energy = this.property?.dpe?.energyClass?.toUpperCase();
    const colors: Record<string, string> = {
      A: '#00803C',
      B: '#54B345',
      C: '#CED932',
      D: '#FFF200',
      E: '#FCBA08',
      F: '#EB8235',
      G: '#D71D20',
    };
    return (energy && colors[energy]) || '#9ca3af';
  });

  onAccept(): void {
    this.dialogRef.close('accept');
  }

  onDecline(): void {
    this.dialogRef.close('decline');
  }

  onClose(): void {
    this.dialogRef.close();
  }
}
